import React, { ReactNode, FocusEvent, useState, useEffect, ChangeEvent } from 'react';
import classNames from 'classnames';
import ClickAwayListener from 'react-click-away-listener';
import { PartialsBackgroundVariants } from './PartialsBackgroundVariants';

export interface InputProps<T> {
  label: string;
  name: string;
  onValueChange: (inputValue: T) => void;
  externalValue?: string;
  placeholder?: string;
  className?: string;
  error?: string;
  prefixIcon?: ReactNode;
  postfixIcon?: ReactNode
  required?: boolean;
  requiredText?: string | ReactNode | Element;
  type?: "text" | "number" | "email" | "password" | "tel";
  inputmode?:
  | "none"
  | "text"
  | "tel"
  | "url"
  | "email"
  | "numeric"
  | "decimal"
  | "search";
  autocomplete?: string;
  pattern?: string;
  autoFocus?: boolean;
  backgroundVariant?: PartialsBackgroundVariants;
  disabled?: boolean;
};

export const Input = <T extends {}>(props: InputProps<T>) => {
  const {
    label,
    name,
    error,
    onValueChange,
    className,
    prefixIcon,
    postfixIcon,
    externalValue,
    pattern,
    requiredText,
    autoFocus,
    placeholder,
    required = false,
    type = "text",
    inputmode = "text",
    autocomplete = "",
    backgroundVariant = 'light',
    disabled = false
  } = props;

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const id = `insuranceInput-${name}`;

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!value) {
      setIsInputFocused(false);
    }

    if (!externalValue) {
      setInputValue(value);
      onValueChange(value as unknown as T);
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onValueChange(e.target.value as unknown as T);
  }

  useEffect(() => {
    if (externalValue) {
      setInputValue(externalValue)
    }
  }, [externalValue])

  const inputFiledModifiers = {
    'insuranceInput__field--prefixIcon': prefixIcon,
    'insuranceInput__field--postfixIcon': postfixIcon,
    'insuranceInput__field--light': backgroundVariant === 'light',
    'insuranceInput__field--blueLight': backgroundVariant === 'blue-light',
  }

  const renderPrefixIcon = () => (
    prefixIcon && (
      <span className="insuranceInput__icon">
        {prefixIcon}
      </span>
    )
  )

  const renderLabel = () => (
    <label
      className={classNames('insuranceInput__label', {
        "insuranceInput__label--focus": !!inputValue || isInputFocused || placeholder,
        'insuranceInput__label--prefixIcon': prefixIcon,
      })}
      htmlFor={id}
    >
      {label} {required && requiredText}
    </label>
  )

  const renderPostfixIcon = () => (
    postfixIcon && (
      <span className="insuranceInput__icon">
        {postfixIcon}
      </span>
    )
  )

  const renderError = () => (
    error && (
      <div className='insuranceInput__error'>
        <p id={"errorId-" + id} className="fs-6 insuranceInput__errorText">
          {error}
        </p>
      </div>
    )
  )

  return (
    <ClickAwayListener onClickAway={() => setIsInputFocused(false)}>
      <div className={classNames("insuranceInput", className, {
        "insuranceInput--error": error,
        "insuranceInput--disabled": disabled,
      })}>
        {renderLabel()}
        <div className="insuranceInput__inner">
          {renderPrefixIcon()}
          <input
            className={classNames('insuranceInput__field', {
              ...inputFiledModifiers,
            })}
            onFocus={() => setIsInputFocused(true)}
            onBlur={handleBlur}
            id={id}
            name={name}
            type={type}
            value={externalValue}
            onChange={externalValue !== undefined ? handleChange : undefined}
            inputMode={inputmode ? inputmode : "text"}
            autoComplete={autocomplete ? autocomplete : ""}
            pattern={pattern}
            required={required}
            aria-describedby={"errorId-" + id}
            autoFocus={autoFocus}
            placeholder={placeholder}
            disabled={disabled}
          />
          {renderPostfixIcon()}
        </div>
        {renderError()}
      </div>
    </ClickAwayListener>
  )
}
